import React from 'react';
import { Box, Group, Text, useMantineTheme, useComputedColorScheme } from '@mantine/core';
import { IconShoppingCart } from '@tabler/icons-react';
import { t } from '../../utils/uiTranslations';

// CartButtonProps defines the props needed for the cart button
interface CartButtonProps {
  itemCount: number;
  onClick: () => void;
  currentLanguage?: string;
}

// CartButton renders a cart banner that fits within flex container
// Uses flexbox parent for positioning (no longer uses fixed positioning)
const CartButton: React.FC<CartButtonProps> = React.memo(({ itemCount, onClick, currentLanguage = 'en' }) => {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';

  return (
    <Box
      onClick={itemCount === 0 ? undefined : onClick}
      style={{
        background: itemCount === 0 
          ? (isDarkMode ? theme.colors.dark[6] : theme.colors.brand[3])
          : (isDarkMode ? theme.colors.orange[9] : theme.colors.brand[2]),
        padding: '8px 16px',
        cursor: itemCount === 0 ? 'default' : 'pointer',
        transition: theme.other.animations.transition,
        boxShadow: theme.other.shadows.nav,
        width: '100%', // Full width within flex container
      }}
    >
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <IconShoppingCart 
            size={16} 
            color={itemCount === 0 ? theme.colors.brand[4] : 'white'} 
          />
          <Text 
            size="sm"
            fw={500}
            c={itemCount === 0 ? 'dimmed' : 'white'}
          >
            {itemCount === 0
              ? t('cartTitle', currentLanguage)
              : t('yourOrderWithCount', currentLanguage, { count: itemCount })}
          </Text>
        </Group>
        {itemCount > 0 && (
          <Text size="xs" c="white" fw={500}>
            {t('viewCart', currentLanguage)}
          </Text>
        )}
      </Group>
    </Box>
  );
});

export default CartButton;



